import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import {
  AlertTriangle,
  Save,
  X,
  LogOut,
  Loader2
} from 'lucide-react';

/**
 * Dialog component to warn users about unsaved changes
 *
 * @param {boolean} open - Whether the dialog is open
 * @param {Function} onSaveAndLeave - Callback when user chooses to save and leave
 * @param {Function} onDiscardChanges - Callback when user chooses to discard changes
 * @param {Function} onCancel - Callback when user cancels navigation
 * @param {string} title - Custom dialog title
 * @param {string} message - Custom warning message
 * @param {boolean} showSaveOption - Whether to show the save option (default: true)
 */
const UnsavedChangesDialog = ({
  open,
  onSaveAndLeave,
  onDiscardChanges,
  onCancel,
  title = '未保存的更改',
  message = '您有未保存的更改。离开此页面将丢失这些更改。',
  showSaveOption = true,
}) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Reset saving state when dialog opens/closes
  React.useEffect(() => {
    if (!open) {
      setSaving(false);
      setError('');
    }
  }, [open]);

  const handleSaveAndLeave = async () => {
    if (!onSaveAndLeave) {
      onDiscardChanges();
      return;
    }

    setSaving(true);
    setError('');
    try {
      await onSaveAndLeave();
      setSaving(false);
    } catch (err) {
      setSaving(false);
      setError(err.message || '保存失败，请重试');
    }
  };

  const handleDiscard = () => {
    if (!saving) {
      onDiscardChanges();
    }
  };

  const handleCancel = () => {
    if (!saving) {
      setError('');
      onCancel();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-warning" />
            {title}
          </DialogTitle>
          <DialogDescription>
            {message}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="p-3 bg-muted rounded-md">
            <p className="text-sm text-muted-foreground">
              • 点击"保存并离开"将保存您的更改后再离开
              <br />
              • 点击"放弃更改"将丢失所有未保存的更改
              <br />
              • 点击"取消"将留在当前页面
            </p>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={saving}
          >
            <X className="mr-2 h-4 w-4" />
            取消
          </Button>

          <Button
            variant="outline"
            onClick={handleDiscard}
            disabled={saving}
            className="text-warning border-yellow-600 hover:bg-warning/10"
          >
            <LogOut className="mr-2 h-4 w-4" />
            放弃更改
          </Button>

          {showSaveOption && (
            <Button
              onClick={handleSaveAndLeave}
              disabled={saving}
            >
              {saving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              {saving ? '保存中...' : '保存并离开'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UnsavedChangesDialog;
